// The vocabulary the UI shows, and the order it shows it in.
//
// The events themselves, and the rules that classify them, live in the
// backend; see `opportunity.ts` for the record. What is here is presentation:
// labels, order, and the fallbacks used until `GET /taxonomy` answers.

import {
  endMinute,
  hasEndTime,
  startMinute,
  type Opportunity,
  type OpportunityFormat,
  type RsvpType,
  type VocabularyRef,
} from './opportunity';

// --- topics ----------------------------------------------------------------

/**
 * The 22 topics, keyed as the backend keys them.
 *
 * `GET /taxonomy` is the source; this is what the sidebar shows until it
 * answers, or if it never does. Alphabetical, matching the sidebar mockup's
 * reading order and the vocabulary's own sort order.
 */
export const TOPICS: VocabularyRef[] = [
  { key: 'adaptation', label: 'Adaptation', icon: null },
  { key: 'ai-and-technology', label: 'AI & Technology', icon: null },
  { key: 'arts-and-culture', label: 'Arts & Culture', icon: null },
  { key: 'co2-removal', label: 'CO2 Removal', icon: null },
  { key: 'carbon-markets-and-accounting', label: 'Carbon Markets & Accounting', icon: null },
  { key: 'circular-economy-and-waste', label: 'Circular Economy & Waste', icon: null },
  { key: 'cities-and-built-environment', label: 'Cities & Built Environment', icon: null },
  { key: 'climate-tech-and-startups', label: 'Climate Tech & Startups', icon: null },
  { key: 'communications', label: 'Communications', icon: null },
  { key: 'corporate-sustainability', label: 'Corporate Sustainability', icon: null },
  { key: 'energy', label: 'Energy', icon: null },
  { key: 'environmental-justice', label: 'Environmental Justice', icon: null },
  { key: 'family-friendly', label: 'Family Friendly', icon: null },
  { key: 'fashion', label: 'Fashion', icon: null },
  { key: 'finance', label: 'Finance', icon: null },
  { key: 'food-and-agriculture', label: 'Food & Agriculture', icon: null },
  { key: 'health', label: 'Health', icon: null },
  { key: 'mobility-and-logistics', label: 'Mobility & Logistics', icon: null },
  { key: 'nature-and-biodiversity', label: 'Nature & Biodiversity', icon: null },
  { key: 'philanthropy', label: 'Philanthropy', icon: null },
  { key: 'policy', label: 'Policy', icon: null },
  { key: 'water', label: 'Water', icon: null },
];

// --- "I want to" -----------------------------------------------------------

/** The six goals the site draws. The backend's vocabulary holds more, unused by any event. */
export const GOALS: VocabularyRef[] = [
  { key: 'grow-my-network', label: 'Grow my Network', icon: null },
  { key: 'attend-the-conference', label: 'Attend the Conference', icon: null },
  { key: 'do-something-interactive', label: 'Do Something Interactive', icon: null },
  { key: 'socialize', label: 'Socialize', icon: null },
  { key: 'get-outside', label: 'Get Outside', icon: null },
  { key: 'watch-a-film-or-show', label: 'Watch a Film or Show', icon: null },
];

// --- event-type words ------------------------------------------------------

/** Fallback for `GET /taxonomy`'s `eventTypes`, for the edit form's Format drop-down. */
export const EVENT_TYPES: string[] = [
  'PANEL', 'KEYNOTE', 'FIRESIDE', 'PITCH', 'SHOWCASE', 'CONFERENCE', 'WORKSHOP', 'NETWORKING',
  'SOCIAL', 'OUTDOORS', 'FILM', 'CONCERT', 'HAPPY HOUR', 'HACKATHON', 'AWARDS', 'LIVE SHOW',
  'FESTIVAL', 'TOUR', 'VOLUNTEER', 'BREAKFAST', 'DINNER', 'RECEPTION', 'SUMMIT', 'EXHIBIT',
  'MEETUP', 'LAUNCH', 'WEBINAR', 'BRIEFING', 'TALK',
];

// --- days ------------------------------------------------------------------

export interface DayDef {
  /** Matches `localDate` of an event. */
  key: string;
  /** Button label: "20", or "Pre"/"Post" for the bookend buckets. */
  label: string;
  sublabel: string;
  weekday: string;
  /** True for the seven core days that get their own My Schedule column. */
  core: boolean;
}

export const CORE_DAYS: DayDef[] = [
  { key: '2026-09-20', label: '20', sublabel: 'Sun', weekday: 'Sunday', core: true },
  { key: '2026-09-21', label: '21', sublabel: 'Mon', weekday: 'Monday', core: true },
  { key: '2026-09-22', label: '22', sublabel: 'Tue', weekday: 'Tuesday', core: true },
  { key: '2026-09-23', label: '23', sublabel: 'Wed', weekday: 'Wednesday', core: true },
  { key: '2026-09-24', label: '24', sublabel: 'Thu', weekday: 'Thursday', core: true },
  { key: '2026-09-25', label: '25', sublabel: 'Fri', weekday: 'Friday', core: true },
  { key: '2026-09-26', label: '26', sublabel: 'Sat', weekday: 'Saturday', core: true },
  { key: '2026-09-27', label: '27', sublabel: 'Sun', weekday: 'Sunday', core: true },
];

/** Anything before the 20th, and anything after the 27th. */
export const PRE_WEEK = 'pre';
export const POST_WEEK = 'post';

export const WEEK_START = '2026-09-20';
export const WEEK_END = '2026-09-27';

export function dayBucket(dayKey: string): string {
  if (dayKey < WEEK_START) return PRE_WEEK;
  if (dayKey > WEEK_END) return POST_WEEK;
  return dayKey;
}

// --- time of day -----------------------------------------------------------

export type TimeSlot = 'morning' | 'afternoon' | 'evening' | 'night';

export const TIME_SLOTS: { id: TimeSlot; label: string; hint: string }[] = [
  { id: 'morning', label: 'Morning', hint: 'before 12PM' },
  { id: 'afternoon', label: 'Afternoon', hint: '12PM – 4PM' },
  { id: 'evening', label: 'Evening', hint: '4PM – 7PM' },
  { id: 'night', label: 'Night', hint: '7PM – late' },
];

/**
 * All-day events return null rather than "morning". Many start at 12:00am
 * because the source published no time; bucketing those as morning would hand
 * anyone filtering for morning events a fistful of week-long conferences.
 */
export function timeSlotOf(event: Opportunity): TimeSlot | null {
  const start = startMinute(event);
  if (event.isAllDay || start == null) return null;
  const h = start / 60;
  if (h < 12) return 'morning';
  if (h < 16) return 'afternoon';
  if (h < 19) return 'evening';
  return 'night';
}

// --- other filters ---------------------------------------------------------
//
// Named for what the data actually supports. The original design had "No Paid
// Events", but only 27 of 997 events carry a price signal at all, so that
// filter would have removed 27 and implied the other 970 were free.

export type OtherFilter = 'inPerson' | 'online' | 'freePublic' | 'hideInviteOnly';

export const OTHER_FILTERS: { id: OtherFilter; label: string }[] = [
  { id: 'inPerson', label: 'In-Person Only' },
  { id: 'online', label: 'Online Only' },
  { id: 'freePublic', label: 'Free & Open to Public' },
  { id: 'hideInviteOnly', label: 'Hide Invite-Only' },
];

/** The formats each filter allows. Selecting both leaves only what is in each: hybrid. */
export const FORMATS_FOR: Partial<Record<OtherFilter, OpportunityFormat[]>> = {
  inPerson: ['in_person', 'hybrid'],
  online: ['virtual', 'hybrid'],
};

/** The RSVP types each filter allows. */
export const RSVP_TYPES_FOR: Partial<Record<OtherFilter, RsvpType[]>> = {
  // Both genuinely-public values. Needing to sign up first does not make an
  // event less open to you — it is still one anybody may attend — and the
  // badge on the card says which of the two it is.
  freePublic: ['open_to_public', 'registration_required'],
  hideInviteOnly: ['open_to_public', 'registration_required', 'apply_to_attend', 'paid', 'see_listing'],
};

export function matchesOtherFilter(event: Opportunity, id: OtherFilter): boolean {
  const formats = FORMATS_FOR[id];
  if (formats) return formats.includes(event.format);
  if (id === 'hideInviteOnly') return event.rsvpType !== 'invite_only';
  const rsvps = RSVP_TYPES_FOR[id];
  return rsvps ? rsvps.includes(event.rsvpType ?? 'see_listing') : true;
}

/**
 * What an "I want to" mark means, said as a thing rather than an intention.
 *
 * The goals are phrased from the planner's side — "Grow my Network" is what
 * *you* want. On a card the icon is describing the *event*, and reading
 * someone else's intention back at them is a small jolt. These are the same
 * six categories in the second person's absence.
 */
export const GOAL_TOOLTIP: Record<string, string> = {
  'grow-my-network': 'Networking event',
  'attend-the-conference': 'Conference event',
  'do-something-interactive': 'Interactive event',
  socialize: 'Social event',
  'get-outside': 'Outdoor event',
  'watch-a-film-or-show': 'Film screening or show',
};

/** Falls back to the goal's own label, so an unmapped goal still says something. */
export const goalTooltip = (goal: VocabularyRef): string => GOAL_TOOLTIP[goal.key] ?? goal.label;

// --- formatting ------------------------------------------------------------

export function formatTime(minutes: number | null): string {
  if (minutes == null) return '';
  const h24 = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = h24 < 12 ? 'AM' : 'PM';
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? `${h}:00 ${suffix}` : `${h}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "10:00 AM - 4:00 PM", or just the start when the end was never published. */
export function formatTimeRange(event: Opportunity): string {
  if (event.isAllDay) return 'All day';
  const start = formatTime(startMinute(event));
  if (!hasEndTime(event)) return start;
  return `${start} - ${formatTime(endMinute(event))}`;
}

/** "MANHATTAN — MIDTOWN" */
export function formatArea(area: string | null): string {
  if (!area) return '';
  return area.replace(/\s*-\s*/, ' — ').toUpperCase();
}

export function longDate(dayKey: string): { full: string; weekday: string } {
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return {
    full: date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' }),
    weekday: date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }),
  };
}

export function mapsUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
